/** The caller's own notification feed. */

import { Link } from "react-router-dom";

import { endpoints } from "@/api/endpoints";
import { Empty, ErrorState, Loading, Notice, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

export function Activity() {
  const { data, error, loading, reload } = useAsync(
    () => endpoints.notifications({ limit: 50 }),
    [],
  );

  if (loading) return <Loading rows={4} label="Loading activity" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const items = data?.items ?? [];
  const unread = items.filter((item) => !item.read_at).length;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Activity</h1>
          <div className="page-header__meta">
            {unread > 0 ? `${unread} unread` : "Nothing unread"}
          </div>
        </div>
        {unread > 0 && (
          <button
            type="button"
            className="button button--small"
            onClick={async () => {
              await endpoints.markAllRead();
              reload();
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="card">
          <Empty title="Nothing yet">
            You will hear about runs you triggered, and about failures on projects you
            administer.
          </Empty>
        </div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {items.map((item) => (
            <div
              key={item.id}
              className="card"
              style={{ borderLeft: item.read_at ? undefined : "3px solid var(--accent)" }}
            >
              <div className="card__body">
                <div className="row row--between">
                  <div>
                    <div style={{ fontWeight: 550 }}>{item.title}</div>
                    <div className="muted" style={{ fontSize: 13, whiteSpace: "pre-line" }}>
                      {item.body}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="faint" style={{ fontSize: 12 }}>
                      <RelativeTime iso={item.created_at} />
                    </span>
                    {!item.read_at && (
                      <button
                        type="button"
                        className="button button--small button--ghost"
                        onClick={async () => {
                          await endpoints.markRead(item.id);
                          reload();
                        }}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
                {item.run_id && item.project_id && (
                  <div style={{ marginTop: 8 }}>
                    <Link to={`/projects/${item.project_id}/runs/${item.run_id}`}>
                      Open the run
                    </Link>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {data && data.total > items.length && (
        <Notice tone="info">
          Showing the most recent {items.length} of {data.total}.
        </Notice>
      )}
    </>
  );
}
